import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import initBootstrapComponents from '../../utils/bootstrap';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import axios from "axios";
import { Riple } from "react-loading-indicators";
import { API_BASE_URL } from '../../constants/api';
import scooterIcon from '../../assets/img/icons/scooter.png';
import wheelchairIcon from '../../assets/img/icons/wheelchair.png';
import tileBackground from '../../assets/img/backgrounds/select-type-tile.png';

const ScooterVehicleList = () => {

    const { eventId, vehicleType } = useParams();
    const navigate = useNavigate();

    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [eventDetails, setEventDetails] = useState(null);
    // Map of vehicle id -> selected slot id
    const [selectedSlotByVehicle, setSelectedSlotByVehicle] = useState({});

    const typeLabel = vehicleType === 'wheelchair' ? 'wheelchair' : 'scooter';
    const typeIcon = vehicleType === 'wheelchair' ? wheelchairIcon : scooterIcon;
    const isAdmin = !!localStorage.getItem('ambassador_token');

    const slotHasAvailability = (slot) => (
        isAdmin ? slot.has_availability : slot.has_public_availability
    );

    useEffect(() => {
        initBootstrapComponents();
        fetchEventDetails();
        fetchVehicles();
    }, []);

    const fetchEventDetails = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/events/${eventId}/details?key=7678356c642c91a255471d162205e0f3`);
            setEventDetails(response.data.data);
        } catch (error) {
            console.error('Error fetching event details:', error);
        }
    };

    const fetchVehicles = async () => {
        setLoading(true);
        const checkLimit = localStorage.getItem('ambassador_token') ? 'admin' : 'customer';
        axios.get(`${API_BASE_URL}/api/events/${eventId}/slots?key=7678356c642c91a255471d162205e0f3&format=vehicles&vehicle_type=${vehicleType}&check_limit=${checkLimit}`)
            .then(response => {
                const vehiclesArray = [];
                Object.values(response.data.data).forEach(vehicleData => {
                    vehiclesArray.push(vehicleData);
                });
                setVehicles(vehiclesArray);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching vehicles:', error);
                setLoading(false);
            });
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getSlotsArray = (slots) => {
        if (Array.isArray(slots)) {
            return slots;
        } else if (typeof slots === 'object' && slots !== null) {
            return Object.values(slots);
        }
        return [];
    };

    // Returns the future, day-deduped, chronologically sorted slots for a vehicle
    const getDaySlots = (vehicleData) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const slots = getSlotsArray(vehicleData.slots)
            .filter(slot => {
                const slotDay = new Date(slot.start_at);
                slotDay.setHours(0, 0, 0, 0);
                return slotDay >= today;
            })
            .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

        // Keep one slot per calendar day (slots are whole-day for scooters/wheelchairs)
        const seenDays = new Set();
        const daySlots = [];
        slots.forEach(slot => {
            const dayKey = new Date(slot.start_at).toDateString();
            if (!seenDays.has(dayKey)) {
                seenDays.add(dayKey);
                daySlots.push(slot);
            }
        });
        return daySlots;
    };

    const handleSelectDay = (vehicleId, slotId) => {
        setSelectedSlotByVehicle(prev => ({ ...prev, [vehicleId]: slotId }));
    };

    const handleBookNow = (vehicleData) => {
        const selectedSlotId = selectedSlotByVehicle[vehicleData.vehicle.id];
        if (!selectedSlotId) return;
        navigate(`/scooter/event/${eventId}/${vehicleType}/vehicle/${vehicleData.vehicle.id}/details?slot=${selectedSlotId}`);
    };

    const vehiclesWithDates = vehicles.filter(v => getDaySlots(v).length > 0);

    return (
        <div className="d-flex flex-column justify-content-between min-vh-100" id="body-container">
            <Header />

            <main>
                <section className="bg-motability-mid-black-5 py-32 pt-lg-96 pb-lg-148">
                    <div className="container">
                        {eventDetails?.name && (
                            <p className="text-motability-black fw-semibold mb-2">
                                {eventDetails.name}{eventDetails.address ? ` – ${eventDetails.address}` : ''}
                            </p>
                        )}
                        <h1 className="text-primary mb-4 mb-lg-5">
                            Select date
                        </h1>

                        {loading ? (
                            <div className="text-center">
                                <Riple color="#0052BE" size="medium" text="" textColor="" />
                            </div>
                        ) : vehiclesWithDates.length === 0 ? (
                            <div className="bg-motability-white border border-motability-mid-black-25 rounded-4 p-4 p-lg-32">
                                <p className="mb-0">No {typeLabel}s are currently available for this event.</p>
                            </div>
                        ) : (
                            vehiclesWithDates.map((vehicleData) => {
                                const vehicle = vehicleData.vehicle;
                                const daySlots = getDaySlots(vehicleData);
                                const selectedSlotId = selectedSlotByVehicle[vehicle.id];

                                return (
                                    <div key={vehicle.id} className="bg-motability-white border border-motability-mid-black-25 rounded-4 p-4 p-lg-32 mb-4">
                                        <div className="row g-4 g-lg-5 align-items-center">
                                            {/* Vehicle icon tile */}
                                            <div className="col-12 col-md-auto">
                                                <div
                                                    className="rounded-3 overflow-hidden d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: '220px',
                                                        height: '220px',
                                                        maxWidth: '100%',
                                                        backgroundImage: `url(${tileBackground})`,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center',
                                                        backgroundRepeat: 'no-repeat'
                                                    }}
                                                >
                                                    <img src={typeIcon} alt="" aria-hidden="true" style={{ width: '70%', maxWidth: '100%', height: 'auto' }} />
                                                </div>
                                            </div>

                                            {/* Title + dates + book now */}
                                            <div className="col">
                                                <h2 className="text-primary h3 mb-2">
                                                    Book a {typeLabel}
                                                </h2>
                                                <p className="mb-3 lh-lg">
                                                    Please select the date you would like to reserve.<br />
                                                    Multiple days require multiple bookings
                                                </p>

                                                <p className="text-primary fw-semibold mb-3">Choose a date</p>
                                                <div className="d-flex flex-wrap gap-2 mb-5">
                                                    {daySlots.map((slot) => (
                                                        slotHasAvailability(slot) ? (
                                                            <button
                                                                key={slot.id}
                                                                type="button"
                                                                className={`btn rounded-pill px-4 py-2 shadow-none ${
                                                                    selectedSlotId === slot.id ? 'btn-primary text-white' : 'bg-motability-light-orange text-primary'
                                                                }`}
                                                                onClick={() => handleSelectDay(vehicle.id, slot.id)}
                                                            >
                                                                {formatDate(slot.start_at)}
                                                            </button>
                                                        ) : (
                                                            <div
                                                                key={slot.id}
                                                                className="d-flex flex-column align-items-start rounded-pill px-4 py-2 bg-motability-mid-black-5"
                                                            >
                                                                <span className="text-motability-black fw-medium">
                                                                    {formatDate(slot.start_at)}
                                                                </span>
                                                                <span className="badge bg-danger text-white px-2 py-1 rounded-pill ff-f37-maulstick mt-1">
                                                                    Fully booked for this date
                                                                </span>
                                                            </div>
                                                        )
                                                    ))}
                                                </div>

                                                <button
                                                    type="button"
                                                    className="btn btn-secondary text-motability-white rounded-pill px-4"
                                                    onClick={() => handleBookNow(vehicleData)}
                                                    disabled={!selectedSlotId}
                                                >
                                                    Book now
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}

                        <Link to={`/scooter/event/${eventId}/select-type`} className="d-inline-block fw-medium text-decoration-none mt-4 mt-lg-5">
                            ‹ <span className="d-inline-block text-decoration-underline">Back</span>
                        </Link>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default ScooterVehicleList;
